import api from './api';
import { ApiResponse } from '../types';

export interface BusinessProfile {
  id: string;
  user_id: string;
  website_url?: string;
  business_name?: string;
  business_description?: string;
  industry?: string;
  target_audience?: {
    age_range?: { min: number; max: number };
    genders?: string[];
    locations?: string[];
    interests?: string[];
    personas?: string[];
    description?: string;
  };
  brand_voice?: 'professional' | 'casual' | 'friendly' | 'formal' | 'bold' | 'inspirational' | 'educational';
  primary_colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    all?: string[];
  };
  logo_url?: string;
  marketing_goals?: string[];
  auto_discovered_competitors?: Competitor[];
  auto_suggested_keywords?: SuggestedKeyword[];
  website_analysis?: WebsiteAnalysis;
  social_handles?: SocialHandles;
  content_preferences?: ContentPreferences;
  ai_context?: string;
  onboarding_step: number;
  onboarding_completed: boolean;
  onboarding_completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface Competitor {
  name: string;
  domain: string;
  description?: string;
  strengths?: string[];
  marketPosition?: string;
}

export interface SuggestedKeyword {
  keyword: string;
  intent?: string;
  difficulty?: string;
  priority?: string;
  suggestedBid?: string;
}

export interface WebsiteAnalysis {
  title?: string;
  description?: string;
  keywords?: string[];
  technologies?: { name: string; category: string }[];
  seo_score?: number;
  seo_issues?: { type: string; message: string }[];
  analyzed_at?: string;
}

export interface SocialHandles {
  instagram?: string;
  facebook?: string;
  twitter?: string;
  linkedin?: string;
  youtube?: string;
  tiktok?: string;
}

export interface ContentPreferences {
  posting_frequency?: string;
  preferred_times?: string[];
  content_types?: string[];
  topics?: string[];
  hashtag_groups?: string[];
}

export interface ConnectedAccount {
  id: string;
  platform: string;
  account_name?: string;
  account_username?: string;
  profile_data?: {
    avatar_url?: string;
    bio?: string;
    followers_count?: number;
    following_count?: number;
    posts_count?: number;
    verified?: boolean;
  };
  connection_status: 'pending' | 'connected' | 'expired' | 'revoked' | 'error';
  can_publish?: boolean;
  can_read_insights?: boolean;
  last_sync_at?: string;
  created_at: string;
}

export interface OnboardingStatus {
  hasProfile: boolean;
  currentStep: number;
  isCompleted: boolean;
  completedAt?: string;
  websiteAnalyzed: boolean;
  briefingCompleted: boolean;
  goalsSet: boolean;
  connectedAccountsCount: number;
  connectedPlatforms: string[];
  competitorsDiscovered: number;
  keywordsSuggested: number;
  suggestedPostsCount: number;
  profile?: BusinessProfile;
}

export interface WebsiteAnalysisResult {
  profile: BusinessProfile;
  analysis: {
    title?: string;
    description?: string;
    colors?: {
      primary?: string;
      secondary?: string;
      accent?: string;
      all?: string[];
    };
    socialLinks?: SocialHandles;
    technologies?: { name: string; category: string }[];
    seoScore?: number;
    aiInsights?: {
      businessType?: string;
      industry?: string;
      mainProducts?: string[];
      targetAudience?: string;
      uniqueSellingPoints?: string[];
      mainTopics?: string[];
      suggestedKeywords?: string[];
      brandVoice?: string;
      competitors?: string[];
    };
  };
}

export interface CompetitorDiscoveryResult {
  competitors: Competitor[];
  marketInsights?: {
    marketSize?: string;
    growthTrend?: string;
    mainDifferentiators?: string[];
  };
}

export interface KeywordSuggestionsResult {
  keywords: SuggestedKeyword[];
  keywordGroups?: {
    name: string;
    keywords: string[];
    intent?: string;
  }[];
}

export interface ContentSuggestionsResult {
  posts: {
    type: string;
    content: string;
    hashtags?: string[];
    bestTime?: string;
    imagePrompt?: string;
    goal?: string;
  }[];
  contentCalendar?: {
    postsPerWeek?: number;
    bestDays?: string[];
    themes?: string[];
  };
}

const onboardingService = {
  getStatus: async (): Promise<OnboardingStatus> => {
    const response = await api.get<ApiResponse<OnboardingStatus>>('/onboarding/status');
    return response.data.data!;
  },

  updateStep: async (step: number): Promise<void> => {
    await api.put('/onboarding/step', { step });
  },

  analyzeWebsite: async (url: string): Promise<{ async: true; jobId: string } | WebsiteAnalysisResult> => {
    const response = await api.post('/onboarding/analyze-website', { url });
    const data = response.data as any;
    if (data.async && data.jobId) {
      return { async: true, jobId: data.jobId };
    }
    return data.data!;
  },

  saveBriefing: async (data: {
    businessName?: string;
    businessDescription?: string;
    industry?: string;
    targetAudience?: any;
    brandVoice?: string;
  }): Promise<BusinessProfile> => {
    const response = await api.post<ApiResponse<{ profile: BusinessProfile }>>('/onboarding/save-briefing', data);
    return response.data.data!.profile;
  },

  saveGoals: async (goals: string[]): Promise<BusinessProfile> => {
    const response = await api.post<ApiResponse<{ profile: BusinessProfile }>>('/onboarding/save-goals', { goals });
    return response.data.data!.profile;
  },

  getConnectedAccounts: async (): Promise<ConnectedAccount[]> => {
    const response = await api.get<ApiResponse<ConnectedAccount[]>>('/onboarding/connected-accounts');
    return response.data.data || [];
  },

  initiateOAuth: async (platform: string, returnTo?: string): Promise<string> => {
    const params = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
    const response = await api.get<ApiResponse<{ authUrl: string }>>(`/onboarding/oauth/${platform}${params}`);
    return response.data.data!.authUrl;
  },

  disconnectPlatform: async (platform: string): Promise<void> => {
    await api.delete(`/onboarding/oauth/${platform}`);
  },

  discoverCompetitors: async (): Promise<CompetitorDiscoveryResult> => {
    const response = await api.post<ApiResponse<CompetitorDiscoveryResult>>('/onboarding/discover-competitors');
    return response.data.data!;
  },

  getKeywordSuggestions: async (): Promise<KeywordSuggestionsResult> => {
    const response = await api.get<ApiResponse<KeywordSuggestionsResult>>('/onboarding/keyword-suggestions');
    return response.data.data!;
  },

  getContentSuggestions: async (): Promise<ContentSuggestionsResult> => {
    const response = await api.get<ApiResponse<ContentSuggestionsResult>>('/onboarding/content-suggestions');
    return response.data.data!;
  },

  completeOnboarding: async (): Promise<{ success: boolean; message?: string }> => {
    const response = await api.post<ApiResponse<any>>('/onboarding/complete');
    return { success: response.data.success, message: response.data.message };
  },

  getProfile: async (): Promise<BusinessProfile | null> => {
    const response = await api.get<ApiResponse<BusinessProfile | null>>('/onboarding/profile');
    return response.data.data ?? null;
  },

  updateProfile: async (updates: Partial<BusinessProfile>): Promise<BusinessProfile> => {
    const response = await api.put<ApiResponse<BusinessProfile>>('/onboarding/profile', updates);
    return response.data.data!;
  }
};

export default onboardingService;
